import { Sprite } from 'pixi.js';
import { Boss, type BossDef } from './boss-base';
import { GAME } from '@/config/game';
import { effectsProfile } from '@/config/fx';
import { getTexture, makeSprite } from '@/assets/resolver';
import type { BulletSystem } from '@/entities/bullets';
import type { TelegraphSink } from '@/render/telegraphs';
import { BOSS_FEEDBACK } from '@/config/boss-feedback';

/**
 * B13 — Splitter. A gelatinous core that HALVES every time its
 * total health crosses a quarter: 1 → 2 → 4 → 8 drifting blobs. Each blob is
 * smaller, faster and fires on its own, so the arena fills with wobbling
 * targets you must clean up before they overwhelm you. docs/02 boss pool.
 * Signatures: quarter-HP division + per-blob spore spray (always); the tier
 * evolve layers fire from the blob nearest the ship.
 */
export const SPLITTER: BossDef = {
  id: 'splitter',
  nameKey: 'boss.splitter',
  tier: 3,
  minRoom: 1,
  baseHp: 1300,
  radius: 56, // generation-0 radius; shrinks per split
  bodyKey: 'boss/splitter/body',
  movement: 'orbit', // unused — custom drift
  contactDamage: 1,
  phases: [{ hpPct: 1.0, patterns: [] }],
  evolve: [
    {
      // Spit: the closest blob lobs a small aimed spore fan
      tier: 2,
      pattern: {
        type: 'aimed', bullet: 'bullet/enemy/spore', count: 3, spreadDeg: 24,
        interval: 3.2, telegraph: 0.45, speed: 230, radius: 10, damage: 1,
      },
    },
    {
      // Membrane pulse: a slow heavy ring from the closest blob
      tier: 3,
      pattern: {
        type: 'radial', bullet: 'bullet/enemy/ring', count: 8, interval: 4.6,
        spinDeg: 20, speed: 110, radius: 10, damage: 2,
      },
    },
  ],
};

const SPLIT_AT = [0.75, 0.5, 0.25]; // total-hp thresholds → double the count
const GEN_RADIUS = [56, 42, 31, 22]; // radius by generation (0..3)
const DRIFT = 70; // blob wander speed (px/s)
const FIRE_INTERVAL = 2.6;
const DRIFT_BOX = 0.4; // half-extent of the wander box around home (× arena size)
const DRIFT_INSET = 60; // the box never reaches closer than this to a wall
const SPRAY_COUNT = 6;
const SPRAY_SPEED = 120;
const SPRAY_SPEED_PER_GEN = 18;
const HIT_FLASH = 0.08;

interface Blob {
  alive: boolean;
  hp: number;
  x: number;
  y: number;
  prevX: number;
  prevY: number;
  vx: number;
  vy: number;
  gen: number;
  fireTimer: number;
  sprite: Sprite;
}

export class SplitterBoss extends Boss {
  private blobs: Blob[] = [];
  private splitIndex = 0;

  constructor(level: number, x: number, y: number, homeX: number, homeY: number) {
    super(SPLITTER, level, x, y, homeX, homeY);
    this.allowReinforce = false;
    this.body.visible = false; // fully custom rendering
    this.blobs.push(this.makeBlob(this.maxHp, homeX, homeY, 0, 1, 0.4));
    this.runners = this.makeRunners([], true); // tier evolve layers (A3)
  }

  private makeBlob(
    hp: number, x: number, y: number, gen: number, dirX: number, dirY: number,
  ): Blob {
    const sprite = makeSprite('boss/splitter/body');
    this.view.addChild(sprite);
    const d = Math.hypot(dirX, dirY) || 1;
    return {
      alive: true, hp, x, y, prevX: x, prevY: y,
      vx: (dirX / d) * DRIFT, vy: (dirY / d) * DRIFT,
      gen, fireTimer: FIRE_INTERVAL * (0.4 + gen * 0.2),
      sprite,
    };
  }

  protected override scaleParts(mul: number): void {
    for (const b of this.blobs) b.hp *= mul;
  }

  private live(): Blob[] {
    return this.blobs.filter((b) => b.alive);
  }

  private radiusOf(gen: number): number {
    return GEN_RADIUS[Math.min(gen, GEN_RADIUS.length - 1)]!;
  }

  override update(
    dt: number, px: number, py: number, bullets: BulletSystem, telegraphs?: TelegraphSink,
  ): void {
    if (this.dead) return;
    this.hitFlash -= dt;
    const speed = this.levelSpeedMul;

    // Soft drift box around home, clamped to the REAL arena (X3) — blobs
    // bounce inside it and never wander through the walls
    const inset = GAME.arena.wallThickness + DRIFT_INSET;
    const minX = Math.max(inset, this.homeX - this.arenaW * DRIFT_BOX);
    const maxX = Math.min(this.arenaW - inset, this.homeX + this.arenaW * DRIFT_BOX);
    const minY = Math.max(inset, this.homeY - this.arenaH * DRIFT_BOX);
    const maxY = Math.min(this.arenaH - inset, this.homeY + this.arenaH * DRIFT_BOX);
    let nearest = Infinity;
    for (const b of this.live()) {
      b.prevX = b.x;
      b.prevY = b.y;
      b.x += b.vx * speed * dt;
      b.y += b.vy * speed * dt;
      if (b.x < minX || b.x > maxX) b.vx *= -1;
      if (b.y < minY || b.y > maxY) b.vy *= -1;
      b.x = Math.max(minX, Math.min(maxX, b.x));
      b.y = Math.max(minY, Math.min(maxY, b.y));
      // Boss reference point = nearest blob (auto-aim / homing)
      const dist = Math.hypot(b.x - px, b.y - py);
      if (dist < nearest) { nearest = dist; this.x = b.x; this.y = b.y; }
      // Per-blob fire: a small radial spray on the tier tempo
      b.fireTimer -= dt * this.intensity;
      if (b.fireTimer <= 0) {
        b.fireTimer = FIRE_INTERVAL;
        const shotSpeed = (SPRAY_SPEED + b.gen * SPRAY_SPEED_PER_GEN) * this.bulletSpeedMul;
        for (let i = 0; i < SPRAY_COUNT; i++) {
          const a = (Math.PI * 2 * i) / SPRAY_COUNT + b.gen;
          bullets.spawn({
            x: b.x, y: b.y, dirX: Math.cos(a), dirY: Math.sin(a),
            speed: shotSpeed, life: 5, radius: 9, damage: 1,
            friendly: false, texKey: 'bullet/enemy/spore', rotates: false,
          });
        }
      }
    }
    this.fireRunners(dt, px, py, bullets, telegraphs);
  }

  /** Each living blob divides into two diverging children of half health. */
  private splitAll(): void {
    const next: Blob[] = [];
    for (const b of this.blobs) {
      if (!b.alive) continue;
      b.alive = false;
      b.sprite.visible = false;
      const gen = b.gen + 1;
      const a = Math.atan2(b.vy, b.vx) + Math.PI / 2;
      const hp = b.hp / 2;
      next.push(this.makeBlob(hp, b.x, b.y, gen, Math.cos(a), Math.sin(a)));
      next.push(this.makeBlob(hp, b.x, b.y, gen, -Math.cos(a), -Math.sin(a)));
      this.events?.burst(b.x, b.y, BOSS_FEEDBACK.splitterSplit);
    }
    this.blobs.push(...next);
  }

  // ---- Collision: many bodies, each with its OWN health (docs/12 L1) ----

  override absorbBullets(bullets: BulletSystem): { hits: number; dealt: number } {
    let hits = 0;
    let dealt = 0;
    for (const b of this.live()) {
      hits += bullets.collideCircle(true, b.x, b.y, this.radiusOf(b.gen), (dmg) => {
        this.hitFlash = HIT_FLASH;
        if (this.damageLocked || !b.alive) return 0; // reinforcement squad first (X9)
        const here = Math.min(b.hp, dmg);
        b.hp -= dmg;
        dealt += here;
        if (b.hp <= 0) { b.alive = false; b.sprite.visible = false; }
        return here; // this blob's own loss
      });
    }
    this.syncHp();
    return { hits, dealt };
  }

  override takeDamage(amount: number): boolean {
    if (this.dead || this.damageLocked) return false;
    const target = this.live().sort((a, b) => a.hp - b.hp)[0];
    if (!target) return false;
    target.hp -= amount;
    this.hitFlash = HIT_FLASH;
    if (target.hp <= 0) { target.alive = false; target.sprite.visible = false; }
    this.syncHp();
    return this.dead;
  }

  private syncHp(): void {
    this.hp = Math.max(
      0, this.blobs.reduce((s, b) => s + (b.alive ? Math.max(0, b.hp) : 0), 0), // unrounded
    );
    if (this.live().length === 0) { this.dead = true; return; }
    // Cross a quarter threshold → every living blob divides once
    while (this.splitIndex < SPLIT_AT.length && this.hpPct <= SPLIT_AT[this.splitIndex]!) {
      this.splitIndex++;
      this.splitAll();
    }
  }

  /** Closest living blob the blast touches. */
  private blobInBlast(x: number, y: number, radius: number): Blob | null {
    let best: Blob | null = null;
    let bestD = Infinity;
    for (const b of this.blobs) {
      if (!b.alive) continue;
      const d = Math.hypot(b.x - x, b.y - y);
      if (d < radius + this.radiusOf(b.gen) && d < bestD) {
        bestD = d;
        best = b;
      }
    }
    return best;
  }

  override hitByAoe(x: number, y: number, radius: number): boolean {
    return this.blobInBlast(x, y, radius) !== null;
  }

  /** Explosions hurt the blob INSIDE the blast, not the weakest one (X5). */
  override applyAoe(x: number, y: number, radius: number, damage: number): number {
    if (this.dead || this.damageLocked) return 0;
    const b = this.blobInBlast(x, y, radius);
    if (!b) return 0;
    this.markAoe(b.x, b.y, this.radiusOf(b.gen));
    const dealt = Math.min(b.hp, damage);
    b.hp -= damage;
    this.hitFlash = HIT_FLASH;
    if (b.hp <= 0) { b.alive = false; b.sprite.visible = false; }
    this.syncHp();
    return dealt;
  }

  override touchesPlayer(px: number, py: number, playerRadius: number): boolean {
    return this.live().some(
      (b) => Math.hypot(b.x - px, b.y - py) < this.radiusOf(b.gen) + playerRadius,
    );
  }

  override render(alpha: number): void {
    this.view.position.set(0, 0);
    for (const b of this.live()) {
      b.sprite.position.set(
        b.prevX + (b.x - b.prevX) * alpha, b.prevY + (b.y - b.prevY) * alpha,
      );
      const punch = this.hitFlash > 0 ? effectsProfile().bossPunch : 1;
      b.sprite.scale.set((this.radiusOf(b.gen) * 2) / 150 * punch);
      b.sprite.texture = getTexture('boss/splitter/body', 0);
      b.sprite.tint = this.levelTint;
    }
  }
}
